import {z} from '@hono/zod-openapi';

import {BUSINESS_CASE_SIGNAL_CODES} from '../constants/BUSINESS_CASE_SIGNAL_CODES';
import {BUSINESS_CASE_SIGNAL_EVIDENCE_OMISSIONS} from '../constants/BUSINESS_CASE_SIGNAL_EVIDENCE_OMISSIONS';
import {BUSINESS_CASE_SIGNAL_SEVERITIES} from '../constants/BUSINESS_CASE_SIGNAL_SEVERITIES';
import {BUSINESS_CASE_TIMELINE_EVENT_KINDS} from '../constants/BUSINESS_CASE_TIMELINE_EVENT_KINDS';

const businessCaseSignalCodeSchema = z.enum(BUSINESS_CASE_SIGNAL_CODES);
const unpaidBusinessCaseSignalCodeSchema = businessCaseSignalCodeSchema.extract([
  'depositUnpaid',
  'invoiceUnpaid',
]);
const generalBusinessCaseSignalCodeSchema = businessCaseSignalCodeSchema.exclude([
  'depositUnpaid',
  'invoiceUnpaid',
]);
const businessCaseSignalShape = {
  severity: z.enum(BUSINESS_CASE_SIGNAL_SEVERITIES),
  evaluatedAt: z.string().min(1),
  evidenceIncomplete: z.boolean(),
  evidenceOmissions: z.array(z.enum(BUSINESS_CASE_SIGNAL_EVIDENCE_OMISSIONS)).default([]),
};
const generalBusinessCaseSignalEvidenceSchema = z.object({
  waitingSinceHours: z.number().int().nonnegative().optional(),
  waitingSinceAt: z.string().min(1).optional(),
  inactiveForDays: z.number().int().nonnegative().optional(),
  lastActivityAt: z.string().min(1).optional(),
  triggeringEventId: z.string().min(1).optional(),
  overdueTaskCount: z.number().int().nonnegative().optional(),
  overdueBySinceDays: z.number().int().nonnegative().optional(),
  oldestOverdueAt: z.string().min(1).optional(),
  engagementKind: z.enum(BUSINESS_CASE_TIMELINE_EVENT_KINDS).optional(),
  offerSentAt: z.string().min(1).optional(),
  offerOpenedAt: z.string().min(1).optional(),
  offerValidTo: z.string().min(1).optional(),
  daysUntilExpiry: z.number().int().nonnegative().optional(),
  expiredForDays: z.number().int().nonnegative().optional(),
  testDriveAt: z.string().min(1).optional(),
  daysSinceTestDrive: z.number().int().nonnegative().optional(),
  hoursUntilTestDrive: z.number().int().nonnegative().optional(),
  incompleteChecklistItemCount: z.number().int().positive().optional(),
  expectedPurchaseDate: z.string().min(1).optional(),
  daysPastExpectedPurchaseDate: z.number().int().nonnegative().optional(),
  customerRequestedPrice: z.number().nonnegative().optional(),
  maximumBuyingPrice: z.number().nonnegative().optional(),
});
const unpaidBusinessCaseSignalEvidenceSchema = generalBusinessCaseSignalEvidenceSchema.extend({
  amount: z.number().optional(),
  currency: z.string().min(1).optional(),
  dueOn: z.string().min(1).optional(),
  issuedAt: z.string().min(1).optional(),
  unpaidForDays: z.number().int().nonnegative(),
});

export const assistantBusinessCaseSignalSchema = z
  .discriminatedUnion('code', [
    z.object({
      ...businessCaseSignalShape,
      code: unpaidBusinessCaseSignalCodeSchema,
      evidence: unpaidBusinessCaseSignalEvidenceSchema,
    }),
    z.object({
      ...businessCaseSignalShape,
      code: generalBusinessCaseSignalCodeSchema,
      evidence: generalBusinessCaseSignalEvidenceSchema,
    }),
  ])
  .openapi('AssistantBusinessCaseSignal');

export type AssistantBusinessCaseSignal = z.infer<typeof assistantBusinessCaseSignalSchema>;
